import { MutationCache, QueryClient } from '@tanstack/react-query';

import { toast } from '../components/ui/Toast';

import { ApiError } from './api/client';
import { errorMessage } from './format';

/**
 * Shared React Query client. Auth errors are never retried (a refresh already
 * happened in the API layer); transient errors get a couple of retries.
 *
 * Every mutation failure surfaces a toast by default via `mutationCache`, so
 * no call site has to wire up error feedback by hand — this is deliberately
 * additive to any inline `Alert` a component already renders.
 */
export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      toast.error(errorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isAuthError || error.status < 500)) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Centralised query keys so invalidation stays consistent and typo-proof. */
export const queryKeys = {
  me: ['auth', 'me'] as const,
  clients: {
    all: ['clients'] as const,
    list: (params: Record<string, unknown>) => ['clients', 'list', params] as const,
    detail: (id: string) => ['clients', 'detail', id] as const,
    timeline: (id: string) => ['clients', 'timeline', id] as const,
  },
  assessments: {
    all: ['assessments'] as const,
    listByClient: (clientId: string) => ['assessments', 'byClient', clientId] as const,
    detail: (id: string) => ['assessments', 'detail', id] as const,
  },
  profiles: {
    byAssessment: (assessmentId: string) => ['profiles', 'byAssessment', assessmentId] as const,
  },
  questionnaires: {
    all: ['questionnaires'] as const,
    listByClient: (clientId: string) => ['questionnaires', 'byClient', clientId] as const,
    detail: (id: string) => ['questionnaires', 'detail', id] as const,
  },
  notes: {
    all: ['notes'] as const,
    listByClient: (clientId: string) => ['notes', 'byClient', clientId] as const,
  },
  fingerprintAnalyses: {
    all: ['fingerprintAnalyses'] as const,
    listByClient: (clientId: string) => ['fingerprintAnalyses', 'byClient', clientId] as const,
    report: (analysisId: string) => ['fingerprintAnalyses', 'report', analysisId] as const,
  },
  guardianships: {
    all: ['guardianships'] as const,
    byClient: (clientId: string) => ['guardianships', 'byClient', clientId] as const,
  },
  contacts: {
    all: ['contacts'] as const,
    listByClient: (clientId: string) => ['contacts', 'byClient', clientId] as const,
  },
  reports: {
    all: ['reports'] as const,
    /** Prefix matching every `list(params)` variant regardless of filters —
     * unlike `all` (which also matches `detail`/`job`/`analytics`), this
     * lets a mutation invalidate just the reports list without touching an
     * actively-open report's own detail query. See `useSaveContentOverrides`
     * in `useReports.ts`. */
    lists: ['reports', 'list'] as const,
    list: (params: Record<string, unknown>) => ['reports', 'list', params] as const,
    detail: (id: string) => ['reports', 'detail', id] as const,
    job: (jobId: string) => ['reports', 'job', jobId] as const,
    analytics: ['reports', 'analytics'] as const,
  },
  templates: {
    all: ['templates'] as const,
    detail: (id: string) => ['templates', 'detail', id] as const,
    active: ['templates', 'active'] as const,
  },
  questionnaireTemplates: {
    all: ['questionnaireTemplates'] as const,
    detail: (id: string) => ['questionnaireTemplates', 'detail', id] as const,
    active: ['questionnaireTemplates', 'active'] as const,
  },
  htmlTemplates: {
    all: (category?: string) => ['htmlTemplates', 'all', category ?? null] as const,
    // Stable prefixes for invalidateQueries — TanStack Query matches by
    // prefix, so these invalidate every category variant of all()/active().
    allPrefix: ['htmlTemplates', 'all'] as const,
    activePrefix: ['htmlTemplates', 'active'] as const,
    detail: (id: string) => ['htmlTemplates', 'detail', id] as const,
    active: (category: string) => ['htmlTemplates', 'active', category] as const,
  },
  availability: {
    all: ['availability'] as const,
  },
  bookings: {
    all: ['bookings'] as const,
    byClient: (clientId: string) => ['bookings', 'byClient', clientId] as const,
    range: (from: string, to: string) => ['bookings', 'range', from, to] as const,
  },
  notifications: {
    all: ['notifications'] as const,
  },
  roles: {
    all: ['roles'] as const,
  },
  team: {
    all: ['team'] as const,
  },
  leads: {
    all: ['leads'] as const,
    list: (params: Record<string, unknown>) => ['leads', 'list', params] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
  },
  packages: {
    all: ['packages'] as const,
    adminAll: ['packages', 'admin-all'] as const,
  },
  features: {
    all: ['features'] as const,
  },
  bankAccounts: {
    all: ['bank-accounts'] as const,
  },
  reportSectionSettings: {
    all: ['report-section-settings'] as const,
  },
  payments: {
    all: ['payments'] as const,
    list: (params: Record<string, unknown>) => ['payments', 'list', params] as const,
    detail: (id: string) => ['payments', 'detail', id] as const,
    slips: (id: string) => ['payments', 'slips', id] as const,
  },
  fingerprintImages: {
    all: ['fingerprintImages'] as const,
    listByClient: (clientId: string) => ['fingerprintImages', 'byClient', clientId] as const,
  },
  resources: {
    all: ['resources'] as const,
    job: (jobId: string) => ['resources', 'job', jobId] as const,
    customizationJob: (jobId: string) => ['resources', 'customizationJob', jobId] as const,
    latestCustomizationJob: (resourceId: string) =>
      ['resources', 'latestCustomizationJob', resourceId] as const,
    versions: (rootResourceId: string) => ['resources', 'versions', rootResourceId] as const,
  },
  resourceGroups: {
    all: ['resourceGroups'] as const,
  },
  auditLog: {
    all: ['auditLog'] as const,
    list: (params: Record<string, unknown>) => ['auditLog', 'list', params] as const,
  },
};
